import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import { useChartPeriod } from '@/hooks';
import { tokensMapping } from '@/utils/tokens';
import {
  CurrencyValue,
  DisplaySwitch,
  Flex,
  LineChart,
  formatNumber,
  formatPercentage
} from '@blockanalitica/ui';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

export default function HistoricTabs({ collateralSymbol }) {
  const { urn } = useParams();
  const ilk = 'LSE-MKR-A';

  const [activeDisplayOption, setActiveDisplayOption] = useState('debt');
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [, updateChartConfigWithPeriod] = useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();
  const displayOptions = useMemo(
    () => [
      { key: 'debt', value: 'Debt' },
      { key: 'collateral', value: 'Collateral' },
      { key: 'cr', value: 'Collateral Ratio' }
    ],
    []
  );

  const [data, error] = useApi({
    url: `/groups/seal/ilks/${ilk}/cdps/${urn}/historic/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  if (error) {
    throw error;
  }

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );

  const yScaleValueFormatter = useMemo(() => {
    return (value) => {
      switch (activeDisplayOption) {
        case 'cr':
          return `${formatPercentage(value, { maximumFractionDigits: 5 })}`;
        default:
          return `${formatNumber(value)}`;
      }
    };
  }, [activeDisplayOption]);

  const valueFormatter = useMemo(() => {
    function formatValue(value: string) {
      switch (activeDisplayOption) {
        case 'cr':
          return `${formatPercentage(Number(value))}`;
        case 'debt':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: 'USDS',
                iconSize: '3'
              }}
            />
          );
        case 'collateral':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping(collateralSymbol),
                iconSize: '3'
              }}
            />
          );
      }
    }

    return formatValue;
  }, [activeDisplayOption, collateralSymbol]);
  if (data) {
    return (
      <Flex flexDirection="column" gap="xs">
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />
        <LineChart
          key={activeDisplayOption}
          data={data.historic}
          datasets={[
            {
              xField: 'date',
              yField: activeDisplayOption
            }
          ]}
          valueFormatter={valueFormatter}
          yScaleValueFormatter={yScaleValueFormatter}
          timeFilter={timeFilter}
          config={config}
        />
      </Flex>
    );
  }

  return <ChartSkeleton />;
}
